import React from 'react'
import Badge from '@amzn/meridian/badge'
import Tab, { TabGroup } from '@amzn/meridian/tab'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Box from '@amzn/meridian/box'
import Icon from '@amzn/meridian/icon'
import filterTokens from '@amzn/meridian-tokens/base/icon/filter'
import filterFilledTokens from '@amzn/meridian-tokens/base/icon/filter-filled'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { ActionCenterTab } from '../types'
import { ALL_STORES_TAB, CURRENT_STORE_TAB, FILTER_HEADING, LIST_MAX_ITEMS, STORE_LOAD_ERROR, TRUNCATED_NOTICE, VIEW_ONLY_MESSAGE, VIEW_ONLY_TITLE } from '../constants'

interface IStoreTabsProps {
    activeTab: ActionCenterTab
    allStoresCount: number
    currentStoreCount: number
    /** Without a current store the "Current store" tab has nothing to show and is disabled. */
    hasCurrentStore: boolean
    onChange: (tab: ActionCenterTab) => void
    /** Show the store-filter icon beside the tabs (only meaningful on the "All stores" tab). */
    canFilter?: boolean
    /** A store filter is applied — the icon is filled to signal it. */
    filterActive?: boolean
    /** Open the store filter sheet. */
    onFilterTap?: () => void
    /** Whether the filter sheet is open; drives aria-expanded on the trigger. */
    filterOpen?: boolean
}

/**
 * "All stores" / "Current store" switch. The inactive tab shows its unread count; the active tab
 * shows none (Meridian renders an undefined value as a compact dot, so the badge is omitted, not blanked).
 *
 * When `canFilter` is set a filter icon sits to the right of the tabs; tapping it opens the store
 * filter sheet. The icon is a native button (not Meridian Button) so its label and test id land
 * directly on the DOM. It is shown only on the "All stores" tab, since "Current store" is a single store.
 */
export const StoreTabs: React.FC<IStoreTabsProps> = ({ activeTab, allStoresCount, currentStoreCount, hasCurrentStore, onChange, canFilter = false, filterActive = false, filterOpen = false, onFilterTap }) => {
    const [bundle] = useBundle(BUNDLE_NAME)
    return (
        <div className="action-center-tabs" data-testid="store-tabs">
            <Row width="100%" alignmentVertical="center" spacing="200" widths={canFilter ? ['fill', 'fit'] : ['fill']}>
                <TabGroup value={activeTab} onChange={(value) => onChange(value as ActionCenterTab)} fill="tabs">
                    <Tab value="all-stores" data-testid="tab-all-stores">
                        <Row spacing="100" alignmentHorizontal="center" alignmentVertical="center">
                            <Text>{t(bundle, ALL_STORES_TAB)}</Text>
                            {activeTab !== 'all-stores' && <Badge type="theme" value={allStoresCount} data-testid="badge-all-stores" />}
                        </Row>
                    </Tab>
                    <Tab value="current-store" data-testid="tab-current-store" disabled={!hasCurrentStore}>
                        <Row spacing="100" alignmentHorizontal="center" alignmentVertical="center">
                            <Text>{t(bundle, CURRENT_STORE_TAB)}</Text>
                            {activeTab !== 'current-store' && <Badge type="theme" value={currentStoreCount} data-testid="badge-current-store" />}
                        </Row>
                    </Tab>
                </TabGroup>
                {canFilter && (
                    <button
                        type="button"
                        className={`action-center-filter-button${filterActive ? ' action-center-filter-button--active' : ''}`}
                        onClick={onFilterTap}
                        data-testid="store-filter-button"
                        aria-label={t(bundle, FILTER_HEADING)}
                        aria-haspopup="dialog"
                        aria-expanded={filterOpen}
                    >
                        <Icon tokens={filterActive ? filterFilledTokens : filterTokens} />
                    </button>
                )}
            </Row>
        </div>
    )
}

/** Shown on the "All stores" tab: cards for other stores cannot be actioned from here. */
export const ViewOnlyNotice: React.FC = () => {
    const [bundle] = useBundle(BUNDLE_NAME)
    return (
        <Box type="outline" width="100%" spacingInset="300 400" className="action-center-view-only" data-testid="view-only-notice">
            <Text type="h100">{t(bundle, VIEW_ONLY_TITLE)}</Text>
            <Text type="b200" color="secondary">{t(bundle, VIEW_ONLY_MESSAGE)}</Text>
        </Box>
    )
}

/**
 * Shown when the tab's read stopped at LIST_MAX_ITEMS. The immediate-attention section is still
 * complete (CRITICAL and HIGH are topped up separately); only the accordion is cut to the newest
 * items, so the note says exactly that.
 */
export const TruncatedNotice: React.FC = () => {
    const [bundle] = useBundle(BUNDLE_NAME)
    return (
        <Box type="fill" width="100%" spacingInset="300 400" className="action-center-truncated" data-testid="truncated-notice">
            <Text type="b200" color="secondary">{t(bundle, TRUNCATED_NOTICE, { count: LIST_MAX_ITEMS })}</Text>
        </Box>
    )
}

/**
 * Shown in place of the "Current store" list when only that store's read failed. The unread
 * inbox loaded, so the "All stores" tab and the bell count stay usable rather than the whole
 * page collapsing to an error.
 */
export const StoreLoadErrorNotice: React.FC = () => {
    const [bundle] = useBundle(BUNDLE_NAME)
    return (
        <Box type="outline" width="100%" spacingInset="300 450" data-testid="store-load-error">
            <Text type="b300" color="error">{t(bundle, STORE_LOAD_ERROR)}</Text>
        </Box>
    )
}
